import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Container, Typography, Box, Button, Paper, Stack, Link,
} from '@mui/material';
import { initKeycloak } from '../keycloak';
import { commercialGet } from '../utils/commercialApi';
import { statusColor, statusLabel, INK } from '../utils/commercialColors';
import KioskCard from './commercial/KioskCard';
import FacilityTransferCard from './facilityTransfer/FacilityTransferCard';

export default function FacilityDetail() {
  const { id } = useParams();
  const [kcInstance, setKcInstance] = useState(null);
  const [token, setToken] = useState('');
  const [forbidden, setForbidden] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [facility, setFacility] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const loginRequestedRef = useRef(false);

  useEffect(() => {
    (async () => {
      const kc = await initKeycloak();
      window.history.replaceState(null, '', window.location.origin + window.location.pathname);

      if (!kc.authenticated) {
        if (loginRequestedRef.current) return;
        loginRequestedRef.current = true;
        kc.login({ redirectUri: window.location.href });
        return;
      }
      if (!kc.hasRealmRole('ROLE_ADMIN') && !kc.hasRealmRole('ROLE_SALES')) {
        setForbidden(true);
        setLoading(false);
        return;
      }
      setKcInstance(kc);
      setToken(kc.token ?? '');
    })().catch(console.error);
  }, []);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    commercialGet(`/facilities/${id}`, token)
      .then((data) => {
        setFacility(data.facility);
        setTimeline(data.timeline || []);
      })
      .catch((err) => setError(err.message || 'Erreur de chargement'))
      .finally(() => setLoading(false));
  }, [token, id]);

  if (forbidden) {
    return <Typography sx={{ mt: 4, textAlign: 'center' }}>Accès refusé.</Typography>;
  }
  if (loading) {
    return <Typography sx={{ mt: 4, textAlign: 'center' }}>Chargement…</Typography>;
  }
  if (error) {
    return <Typography sx={{ mt: 4, textAlign: 'center', color: '#d03b3b' }}>{error}</Typography>;
  }
  if (!facility) {
    return <Typography sx={{ mt: 4, textAlign: 'center' }}>Établissement introuvable.</Typography>;
  }

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Link component={RouterLink} to="/admin/commercial" sx={{ display: 'block', mb: 1 }}>
            ← Retour au dashboard
          </Link>
          <Typography variant="h4">{facility.facility_name}</Typography>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: statusColor(facility.status) }} />
            <Typography variant="body2" sx={{ color: INK.secondary }}>
              {statusLabel(facility.status)} — {facility.zone || 'zone non définie'}
            </Typography>
          </Stack>
        </Box>
        <Button variant="outlined" onClick={() => kcInstance?.logout({ redirectUri: `${window.location.origin}/` })}>
          Logout
        </Button>
      </Box>

      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Stack spacing={1}>
          <Row label="Ambassadeur" value={facility.agent_name || '—'} />
          <Row label="Canal d'acquisition" value={facility.acquisition_channel || '—'} />
          <Row label="Code referral" value={facility.referral_code || '—'} />
          <Row label="Plan" value={facility.plan_name || '—'} />
          <Row label="MRR" value={facility.mrr != null ? `${Number(facility.mrr).toLocaleString('fr-FR')} €` : '—'} />
        </Stack>
      </Paper>

      <KioskCard facilityId={facility.facility_id} token={token} />

      <Box sx={{ mb: 3 }}>
        <FacilityTransferCard facilityId={facility.facility_id} ownerEmail={facility.owner_email}
          getToken={() => kcInstance?.token ?? token} elevation />
      </Box>

      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>Timeline commerciale</Typography>
        <Stack spacing={1.5}>
          {timeline.map((ev, i) => (
            <Box key={i} sx={{ display: 'flex', gap: 2 }}>
              <Typography variant="body2" sx={{ color: INK.muted, minWidth: 90, fontVariantNumeric: 'tabular-nums' }}>
                {new Date(ev.at).toLocaleDateString('fr-FR')}
              </Typography>
              <Typography variant="body2" sx={{ color: INK.primary }}>{ev.label}</Typography>
            </Box>
          ))}
          {timeline.length === 0 && (
            <Typography variant="body2" sx={{ color: INK.muted }}>Aucun événement pour l'instant.</Typography>
          )}
        </Stack>
      </Paper>
    </Container>
  );
}

function Row({ label, value }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
      <Typography variant="body2" sx={{ color: INK.muted }}>{label}</Typography>
      <Typography variant="body2" sx={{ color: INK.primary, fontWeight: 600 }}>{value}</Typography>
    </Box>
  );
}
